import { Badge } from '@optools/components/ui/badge';
import {
  Feed,
  MediaFeedType,
  Team,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { formatHourMinute } from '@optools/utils/datetime';

import { cn } from './ui/utils';
import { TeamSpot, TeamSpotProps } from './team-spot';

export interface TeamFeedProps {
  feed: Feed | undefined;
  feedType: MediaFeedType.AWAY | MediaFeedType.HOME;
}

function TeamFeed({ feed, feedType }: TeamFeedProps) {
  if (!feed?.postGameShow && !feed?.preGameShow) return null;

  return (
    <div className="flex flex-col gap-y-1" data-testid={`${feedType}-team-shows`}>
      {feed?.preGameShow && (
        <Badge className="w-full justify-center text-xs whitespace-nowrap" variant="outline">
          PRE-{formatHourMinute(feed.preGameShow)}
        </Badge>
      )}
      {feed?.postGameShow && (
        <Badge className="w-full justify-center text-xs whitespace-nowrap" variant="outline">
          POST-GAME
        </Badge>
      )}
    </div>
  );
}

export type MatchupProps = {
  awayTeam: Pick<Team, 'abbreviation' | 'id'> | undefined;
  feeds?: Feed[];
  homeTeam: Pick<Team, 'abbreviation' | 'id'> | undefined;
  withShows?: boolean;
  className?: string;
} & Pick<TeamSpotProps, 'size'>;

export function GameMatchup({
  awayTeam,
  className,
  feeds,
  homeTeam,
  size,
  withShows = true,
}: MatchupProps) {
  const hasShows = feeds?.some((feed) => feed?.postGameShow || feed?.preGameShow);

  return (
    <div className={cn('grid w-max grid-cols-[auto_1fr_auto] gap-y-2', className)}>
      <div
        className="col-span-full grid grid-cols-subgrid items-center justify-items-center gap-x-2"
        data-testid={`game-matchup-${awayTeam?.abbreviation}-${homeTeam?.abbreviation}`}>
        <div data-testid="away-team-container">
          <TeamSpot size={size} team={awayTeam} />
        </div>
        <span className="text-xs font-bold">@</span>
        <div data-testid="home-team-container">
          <TeamSpot size={size} team={homeTeam} />
        </div>
      </div>
      {hasShows && withShows && (
        <div
          className="col-span-full grid grid-cols-subgrid gap-x-4"
          data-testid="game-matchup-shows">
          <div>
            <TeamFeed
              feed={feeds?.find((item) => item.feedType === MediaFeedType.AWAY)}
              feedType={MediaFeedType.AWAY}
            />
          </div>
          {/* Spacer div */}
          <div />
          <div>
            <TeamFeed
              feed={feeds?.find((item) => item.feedType === MediaFeedType.HOME)}
              feedType={MediaFeedType.HOME}
            />
          </div>
        </div>
      )}
    </div>
  );
}
